import { useState, useMemo, useCallback, useEffect } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { PaymentMethod } from '@packlink/packlink-sdk';
import { initDataCollector, initPaypal } from '../../../utils/braintree.js';
import { getPaymentOptionPaypalStyles, getPaymentOptionPaypalEmailStyles, getPaymentOptionPaypalDeleteButton } from './PaymentOptionPaypalStyles.js';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { DeleteVault } from '../../PaymentComponents/DeleteVault/DeleteVault.js';
import { Paypal } from '../../PaymentComponents/Paypal/Paypal.js';
import { LoggerPaymentErrorTagValues } from '../../../types/types.js';

let PaypalLocale = /*#__PURE__*/function (PaypalLocale) {
  PaypalLocale["da_DK"] = "da_DK";
  PaypalLocale["de_DE"] = "de_DE";
  PaypalLocale["en_AU"] = "en_AU";
  PaypalLocale["en_GB"] = "en_GB";
  PaypalLocale["en_US"] = "en_US";
  PaypalLocale["es_ES"] = "es_ES";
  PaypalLocale["fr_CA"] = "fr_CA";
  PaypalLocale["fr_FR"] = "fr_FR";
  PaypalLocale["id_ID"] = "id_ID";
  PaypalLocale["it_IT"] = "it_IT";
  PaypalLocale["ja_JP"] = "ja_JP";
  PaypalLocale["ko_KR"] = "ko_KR";
  PaypalLocale["nl_NL"] = "nl_NL";
  PaypalLocale["no_NO"] = "no_NO";
  PaypalLocale["pl_PL"] = "pl_PL";
  PaypalLocale["pt_BR"] = "pt_BR";
  PaypalLocale["pt_PT"] = "pt_PT";
  PaypalLocale["ru_RU"] = "ru_RU";
  PaypalLocale["sv_SE"] = "sv_SE";
  PaypalLocale["th_TH"] = "th_TH";
  PaypalLocale["zh_CN"] = "zh_CN";
  PaypalLocale["zh_HK"] = "zh_HK";
  PaypalLocale["zh_TW"] = "zh_TW";
  return PaypalLocale;
}({});
let PaypalVerifyErrorCode = /*#__PURE__*/function (PaypalVerifyErrorCode) {
  PaypalVerifyErrorCode["PAYPAL_ACCOUNT_TOKENIZATION_FAILED"] = "PAYPAL_ACCOUNT_TOKENIZATION_FAILED";
  PaypalVerifyErrorCode["PAYPAL_FLOW_FAILED"] = "PAYPAL_FLOW_FAILED";
  PaypalVerifyErrorCode["PAYPAL_POPUP_CLOSED"] = "PAYPAL_POPUP_CLOSED";
  return PaypalVerifyErrorCode;
}({});
var BREADCRUMBS = /*#__PURE__*/function (BREADCRUMBS) {
  BREADCRUMBS["SETUP_ERROR"] = "PayPal setup error";
  BREADCRUMBS["SETUP_SUCCESS"] = "PayPal setup success";
  BREADCRUMBS["VERIFICATION_ERROR"] = "PayPal verification error";
  BREADCRUMBS["VERIFICATION_STARTS"] = "PayPal verification starts";
  BREADCRUMBS["VERIFICATION_SUCCESS"] = "PayPal verification success";
  BREADCRUMBS["VERIFICATION_CANCEL"] = "PayPal verification cancel";
  BREADCRUMBS["VAULT"] = "PayPal vault payment";
  return BREADCRUMBS;
}(BREADCRUMBS || {});
const PaymentOptionPaypal = _ref => {
  let {
    clientBraintree,
    locale,
    cdnUrl,
    amount,
    paypal,
    currency,
    isPaymentBlocked = false,
    isProcessing,
    payButtonText,
    onCheckForExternalError,
    onSetupSuccess,
    onSetupError,
    onVerificationStart,
    onVerificationSuccess,
    onVerificationError,
    onDelete
  } = _ref;
  const [isReady, setIsReady] = useState(false);
  const [isValidatingBraintree, setIsValidatingBraintree] = useState(false);
  const [paypalInstance, setPaypalInstance] = useState();
  const [dataCollector, setDataCollector] = useState();
  const paypalLocale = PaypalLocale[locale] || PaypalLocale[locale.replace('-', '_')] || PaypalLocale.en_GB;
  const isButtonLoading = useMemo(() => !isReady && !paypal || isValidatingBraintree || !!isProcessing, [isReady, paypal, isValidatingBraintree, isProcessing]);
  const handleVerificationStart = useCallback(() => {
    onVerificationStart(BREADCRUMBS.VERIFICATION_STARTS);
    setIsValidatingBraintree(true);
  }, [onVerificationStart]);
  const handleVerificationSuccess = useCallback(verification => {
    setIsValidatingBraintree(false);
    onVerificationSuccess(PaymentMethod.PAYPAL, BREADCRUMBS.VERIFICATION_SUCCESS, verification);
  }, [onVerificationSuccess]);
  const handleVerificationError = useCallback(error => {
    let braintreeError = undefined;
    if (error) {
      braintreeError = {
        code: error.code,
        details: error.details,
        message: 'PayPal verification error',
        type: 'UNKNOWN'
      };
    }
    onVerificationError(PaymentMethod.PAYPAL, BREADCRUMBS.VERIFICATION_ERROR, LoggerPaymentErrorTagValues.PAYMENT_PAYPAL_VERIFICATION, braintreeError);
    setIsValidatingBraintree(false);
  }, [onVerificationError]);
  const handleVerificationCancel = useCallback(() => {
    onVerificationStart(BREADCRUMBS.VERIFICATION_CANCEL);
    setIsValidatingBraintree(false);
  }, [onVerificationStart]);
  const handleDelete = useCallback(() => {
    onDelete(paypal);
  }, [paypal, onDelete]);
  const handleOnPay = useCallback(() => {
    if (isPaymentBlocked) {
      onCheckForExternalError === null || onCheckForExternalError === void 0 ? void 0 : onCheckForExternalError();
      return;
    }
    const verification = {
      deviceData: dataCollector === null || dataCollector === void 0 ? void 0 : dataCollector.deviceData
    };
    onVerificationSuccess(PaymentMethod.PAYPAL, BREADCRUMBS.VAULT, verification);
  }, [onVerificationSuccess, dataCollector === null || dataCollector === void 0 ? void 0 : dataCollector.deviceData]);
  const tokenizePayment = useCallback(() => {
    if (isPaymentBlocked) {
      onCheckForExternalError === null || onCheckForExternalError === void 0 ? void 0 : onCheckForExternalError();
      return;
    }
    handleVerificationStart();
    paypalInstance === null || paypalInstance === void 0 ? void 0 : paypalInstance.tokenize({
      amount,
      currency,
      flow: 'vault',
      locale: paypalLocale
    }, (error, payload) => {
      const deviceData = dataCollector === null || dataCollector === void 0 ? void 0 : dataCollector.deviceData;
      if (error) {
        error.deviceData = deviceData;
        return error.code === PaypalVerifyErrorCode.PAYPAL_POPUP_CLOSED ? handleVerificationCancel() : handleVerificationError(error);
      }
      if (payload) {
        payload.deviceData = deviceData;
      }
      handleVerificationSuccess(payload);
    });
  }, [handleVerificationStart, paypalInstance, amount, currency, paypalLocale, dataCollector === null || dataCollector === void 0 ? void 0 : dataCollector.deviceData, handleVerificationSuccess, handleVerificationCancel, handleVerificationError]);
  useEffectOnce(() => {
    async function createDataCollector() {
      const dataCollector = await initDataCollector(clientBraintree, true);
      setDataCollector(dataCollector);
    }
    createDataCollector();
  });
  useEffect(() => {
    const initializeBraintree = async client => {
      try {
        const paypal = await initPaypal(client);
        setPaypalInstance(paypal);
        onSetupSuccess(BREADCRUMBS.SETUP_SUCCESS);
        setIsReady(true);
      } catch (error) {
        onSetupError(error, BREADCRUMBS.SETUP_ERROR, LoggerPaymentErrorTagValues.PAYMENT_PAYPAL_SETUP, PaymentMethod.PAYPAL);
        setIsReady(false);
      }
    };
    clientBraintree && !paypal && !paypalInstance && initializeBraintree(clientBraintree);
  }, [clientBraintree, onSetupError, onSetupSuccess, paypal, paypalInstance]);
  if (paypal) {
    return jsxs("section", {
      css: getPaymentOptionPaypalStyles,
      children: [jsx("span", {
        css: getPaymentOptionPaypalEmailStyles,
        children: paypal.email
      }), jsx("section", {
        css: getPaymentOptionPaypalDeleteButton,
        children: jsx(DeleteVault, {
          onDelete: handleDelete
        })
      }), jsx(Paypal, {
        isLoading: isButtonLoading,
        cdnUrl: cdnUrl,
        buttonText: payButtonText,
        onClick: handleOnPay
      })]
    });
  } else {
    return jsx(Paypal, {
      isLoading: isButtonLoading,
      cdnUrl: cdnUrl,
      buttonText: payButtonText,
      onClick: tokenizePayment
    });
  }
};

export { PaymentOptionPaypal, PaypalLocale, PaypalVerifyErrorCode };
//# sourceMappingURL=PaymentOptionPaypal.js.map
